import { useEffect, useState } from 'react';
import { useAuth, type UserInfo } from '../contexts/AuthContext';
import { trpc } from '../lib/trpc';
import { useAppResume } from '../hooks/useAppLifecycle';

/** After the app returns to the foreground, poll `auth.me` for this long
 *  so a server-side change that lands around the same moment is caught —
 *  in particular a Stripe webhook that fires while the app is
 *  backgrounded (e.g. after a native link-out checkout completes in an
 *  external browser and the user swaps back). */
const RESUME_POLL_WINDOW_MS = 8_000;
/** Cadence within that window. */
const RESUME_POLL_INTERVAL_MS = 2_000;

/**
 * True when every user field we mirror into AuthContext matches — lets us
 * skip a redundant `setUserInfo` on each resume poll, which would
 * otherwise replace `user` with an equal-but-new object and re-fire the
 * analytics `identify()` (keyed on the `[user]` effect) every tick.
 */
function userInfoEqual(a: UserInfo | null, b: UserInfo): boolean {
  if (!a) return false;
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.subscriptionTier === b.subscriptionTier &&
    a.displayName === b.displayName &&
    a.avatarUrl === b.avatarUrl &&
    a.emailVerified === b.emailVerified &&
    (a.isAdmin ?? false) === (b.isAdmin ?? false) &&
    (a.paymentStatus ?? null) === (b.paymentStatus ?? null)
  );
}

/**
 * Keeps the AuthContext `user` in sync with server-only fields the JWT
 * can't carry — `subscriptionTier` (the DB is authoritative; the token's
 * `tier` claim goes stale the moment a Stripe webhook changes it) and
 * `paymentStatus` (drives the `PastDueBanner`). `setAuthFromTokens`
 * builds `user` from the decoded token, so only `auth.me` (resolved from
 * DB per request) exposes the live values.
 *
 * Two refresh triggers:
 *   - Mount / cold start — the base query (global 5-min staleTime).
 *   - Foreground resume — arms a short polling window and refetches
 *     immediately. This is what makes an upgrade reflect without a
 *     restart on native (where checkout happens in an external browser,
 *     so there's no in-app success screen), and it's deliberately NOT
 *     gated on the current tier: a downgrade/cancellation (Pro→basic)
 *     must be picked up just as promptly as an upgrade. The bounded
 *     window also absorbs the checkout-vs-webhook race — if the webhook
 *     lands a second or two after resume, a follow-up poll catches it.
 *
 * Renders `null`; mount once inside the trpc.Provider tree under
 * AuthProvider (alongside `SyncManagerInit`). Best-effort and non-fatal.
 */
export function PaymentStatusSync() {
  const { isAuthenticated, user, setUserInfo } = useAuth();
  // Epoch ms until which the resume poll stays active; 0 = not polling.
  const [pollUntil, setPollUntil] = useState(0);

  const me = trpc.auth.me.useQuery(undefined, {
    enabled: isAuthenticated,
    // Only polls inside a resume window; false otherwise, so steady-state
    // stays at the mount query's 5-min staleTime.
    refetchInterval: () => (Date.now() < pollUntil ? RESUME_POLL_INTERVAL_MS : false),
  });

  useEffect(() => {
    if (!me.isSuccess) return;
    // Skip no-op replacements so repeated resume polls don't churn
    // AuthContext / re-fire analytics identify().
    if (userInfoEqual(user, me.data)) return;
    setUserInfo(me.data);
  }, [me.isSuccess, me.data, user, setUserInfo]);

  useAppResume(() => {
    if (!isAuthenticated) return;
    setPollUntil(Date.now() + RESUME_POLL_WINDOW_MS);
    // Immediate read on resume; the armed interval covers the follow-ups.
    void me.refetch();
  });

  return null;
}
